"use client";

import { useState, useEffect, useCallback } from "react";
import type { OpenClawData } from "./openclaw";

export type { OpenClawData };
export type {
  OpenClawAgent,
  OpenClawChannel,
  OpenClawCronJob,
  OpenClawMemoryEntry,
  OpenClawTaskEvent,
} from "./openclaw";

interface UseOpenClawResult {
  data: OpenClawData | null;
  error: string | null;
  loading: boolean;
  refresh: () => void;
}

export function useOpenClaw(): UseOpenClawResult {
  const [data, setData] = useState<OpenClawData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch("/api/openclaw");
      if (!res.ok) {
        throw new Error(`OpenClaw API returned ${res.status}`);
      }
      const json = await res.json();
      setData(json);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch OpenClaw data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds for live-ish data
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return { data, error, loading, refresh: fetchData };
}
